import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AdminRoles, PatientStatusCode } from '../../config/DiactiveTypes';

// Virtual status filter that does not come from determinePatientStatus(); it is
// computed from the pending-mail data (see PatientsTable).
export const MAIL_PENDING_STATUS = 'mail_pending';

const allowedSortKeys = [
  'createdAt',
  'name',
  'nickname',
  'id',
  'daysRemaining',
  'pendingQuestionnaires',
  'pendingGlucoseFiles',
  'workoutsThisWeek',
];

// Sort keys that are only valid under specific status filters, because the
// underlying column only appears then. When the selected status doesn't match,
// we reset the sort to avoid an "invisible" active sort.
const conditionalSortKeys = {
  pendingQuestionnaires: [PatientStatusCode.PRE_PENDING, PatientStatusCode.POST_PENDING],
  pendingGlucoseFiles: [PatientStatusCode.PRE_PENDING, PatientStatusCode.POST_PENDING],
  workoutsThisWeek: [PatientStatusCode.NORMAL, PatientStatusCode.LOW_DAYS_REMAINING],
};

const DEFAULT_SORT = { key: 'createdAt', direction: 'desc' };

const isSortKeyValidForStatus = (sortKey, statusCode) => {
  const allowedStatuses = conditionalSortKeys[sortKey];
  if (!allowedStatuses) return true;
  return allowedStatuses.includes(statusCode);
};

export const usePatientsTableUrlState = ({ role, viewAsDoctor = false, doctorModeHospitalId = '' }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const statusCodes = useMemo(() => ([
    PatientStatusCode.PRE_PENDING,
    PatientStatusCode.COUNTDOWN_NOT_STARTED,
    PatientStatusCode.NORMAL,
    PatientStatusCode.LOW_DAYS_REMAINING,
    PatientStatusCode.POST_PENDING,
    PatientStatusCode.STUDY_FINISHED,
    PatientStatusCode.WITHDRAWN,
    PatientStatusCode.UNDETERMINED,
    MAIL_PENDING_STATUS,
  ]), []);

  const selectedHospitalFromUrl = AdminRoles.includes(role) ? (searchParams.get('hospital') ?? '') : '';
  const selectedGroupFromUrl = searchParams.get('group') ?? '';
  const selectedStatusFromUrlRaw = searchParams.get('status') ?? '';
  const selectedStatusFromUrl = statusCodes.includes(selectedStatusFromUrlRaw) ? selectedStatusFromUrlRaw : '';
  const searchQueryFromUrl = searchParams.get('q') ?? '';

  const sortKeyFromUrlRaw = searchParams.get('sortKey') ?? DEFAULT_SORT.key;
  const sortKeyFromUrlAllowed = allowedSortKeys.includes(sortKeyFromUrlRaw) ? sortKeyFromUrlRaw : DEFAULT_SORT.key;
  const sortKeyFromUrl = isSortKeyValidForStatus(sortKeyFromUrlAllowed, selectedStatusFromUrl)
    ? sortKeyFromUrlAllowed
    : DEFAULT_SORT.key;
  const sortDirFromUrl = searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc';

  const [selectedHospital, setSelectedHospital] = useState(selectedHospitalFromUrl);
  const [selectedGroup, setSelectedGroup] = useState(selectedGroupFromUrl);
  const [selectedStatus, setSelectedStatus] = useState(selectedStatusFromUrl);

  const [searchQuery, setSearchQuery] = useState(searchQueryFromUrl);
  const [sortConfig, setSortConfig] = useState({ key: sortKeyFromUrl, direction: sortDirFromUrl });

  useEffect(() => {
    if (!AdminRoles.includes(role) && selectedHospital) {
      setSelectedHospital('');
    }
  }, [role, selectedHospital]);

  useEffect(() => {
    if (!isSortKeyValidForStatus(sortConfig.key, selectedStatus)) {
      setSortConfig(DEFAULT_SORT);
    }
  }, [selectedStatus, sortConfig.key]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (AdminRoles.includes(role)) {
          if (selectedHospital) next.set('hospital', selectedHospital);
          else next.delete('hospital');
        } else {
          next.delete('hospital');
        }

        if (selectedGroup) next.set('group', selectedGroup);
        else next.delete('group');

        if (searchQuery) next.set('q', searchQuery);
        else next.delete('q');

        if (selectedStatus) next.set('status', selectedStatus);
        else next.delete('status');

        if (viewAsDoctor) next.set('viewAsDoctor', '1');
        else next.delete('viewAsDoctor');

        if (doctorModeHospitalId) next.set('doctorHospital', doctorModeHospitalId);
        else next.delete('doctorHospital');

        next.set('sortKey', sortConfig.key);
        next.set('sortDir', sortConfig.direction);
        return next;
      },
      { replace: true }
    );
  }, [selectedHospital, selectedGroup, selectedStatus, role, searchQuery, sortConfig, viewAsDoctor, doctorModeHospitalId, setSearchParams]);

  return {
    statusCodes,
    selectedHospital,
    setSelectedHospital,
    selectedGroup,
    setSelectedGroup,
    selectedStatus,
    setSelectedStatus,
    searchQuery,
    setSearchQuery,
    sortConfig,
    setSortConfig,
  };
};

